import React, { useEffect, useState } from "react";
import { Table, Button, Row, Col, Tooltip, Drawer, Grid } from "antd";
import { EditOutlined, PlusOutlined,   QuestionCircleOutlined} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchContraList, deleteContra } from "../contraThunks";
import { App } from "antd";
import { selectContraList } from "../contraSelectors";
import { setSelectedContra, setIsEditContra } from "../contraSlice";
import dayjs from "dayjs";
import { formatDateOnly, getAccessType } from "../../../lib/functions";
import { DeleteOutlined } from "@ant-design/icons";
import { useIsAdminUser } from "../../../lib/accessList";
import { useTxLock } from "../../../hooks/useTxLock";

const ContraList = () => {
  const { modal } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  let [access_type, access_type_set] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    const access = getAccessType("contra_entry");
    access_type_set(access);
  }, []);
  const isAdminUser = useIsAdminUser();
  const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;
const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();
  const contraList = useSelector(selectContraList);

  const handleDelete = async (contra_id, contra_name) => {
    try {
      await dispatch(deleteContra(contra_id)).unwrap();
      modal.success({
        title: "Contra entry deleted successfully",
      });
      dispatch(fetchContraList());
    } catch (error) {
      modal.warning({
        title: error.message || "Delete failed",
      });
    }
  };

  const columns = [
    {
      title: "NO.",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Contra Code",
      dataIndex: "contra_code",
    },
    {
      title: "Voucher Date",
      dataIndex: "created_date",
      render: (date) => formatDateOnly(date),
    },
    {
      title: "From Account",
      dataIndex: "from_acc_name",
    },
    {
      title: "To Account",
      dataIndex: "to_acc_name",
    },
    {
      title: "Amount",
      dataIndex: "tran_amount",
      render: (v) => Number(v || 0).toFixed(2),
    },
    {
      title: "Narration",
      dataIndex: "narration",
    },
    {
      title: "Created By",
      dataIndex: "name",
    },
  {
  title: "Actions",
  render: (_, record) => {
    const txLocked = isTxLocked(record?.created_date);

    return (
      <>
        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Edit"
          }
        >
          <Button
            type="link"
            disabled={!(isAdminUser || canEdit) || txLocked}
            icon={<EditOutlined />}
            onClick={() => {
              if (txLocked) return;

              dispatch(setSelectedContra(record));
              dispatch(setIsEditContra(true));

              navigate("addNewContra");
            }}
          />
        </Tooltip>

        <Tooltip
          title={
            txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Delete"
          }
        >
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            disabled={!(isAdminUser || canDelete) || txLocked}
            onClick={() => {
              if (txLocked) return;

              modal.confirm({
                title: "Are you sure?",
                content: `This will delete contra entry with amount: ${record.tran_amount}`,
                okText: "Yes, Delete",
                cancelText: "Cancel",
                onOk: () =>
                  handleDelete(record.contra_id, record.tran_amount),
              });
            }}
          />
        </Tooltip>
      </>
    );
  },
}
  ];

  useEffect(() => {
    dispatch(fetchContraList());
  }, [dispatch]);

  return (
    <>
      <Row justify="end" style={{ marginBottom: "32px", marginTop: "32px" }}>
        <Col>
          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </Col>
        <Col>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                className="traic-button"
                icon={<PlusOutlined />}
                style={{
                  minWidth: "112px",
                  height: "36px",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
                onClick={() => {
                  dispatch(setSelectedContra(null));
                  dispatch(setIsEditContra(false));
                  navigate("addNewContra");
                }}
                data-testid="btn-add-new-contra"
              >
                Add New
              </Button>
            </>
          )}
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={contraList}
        rowKey="contra_id"
        scroll={{ x: "max-content" }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: contraList.length,
          showQuickJumper: true,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          onChange: (page) => {
            setCurrentPage(page);
          },
          onShowSizeChange: (_, size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
        }}
      />
      <Drawer
        title="Contra Transaction Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={420}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Contra Transactions
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Contra Transaction module is used to record internal fund
            transfers between cash and bank accounts within the ERP system.
            These entries help maintain accurate accounting balances and transaction tracking.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View contra transaction records</li>
              <li>Use <strong>Add New</strong> to create contra entries</li>
              <li>Edit transaction details when required</li>
              <li>Delete transactions if permitted</li>
              <li>Transfer amounts between accounts</li>
              <li>Track voucher dates and transaction narration</li>
              <li>Monitor accounting movement between accounts</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Contra entries affect account balances immediately.</li>
              <li>From and To account selection should be verified carefully.</li>
              <li>Access permissions control edit and delete actions.</li>
              <li>Transaction history is maintained for auditing and reports.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default ContraList;
